import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { CheckCircle, Circle, FileText, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface ChecklistItem {
  id: string;
  label: string;
  description?: string;
  link?: string;
  linkLabel?: string;
}

interface ProgramChecklistData {
  programId: string;
  programName: string;
  documents: ChecklistItem[];
  steps: ChecklistItem[];
}

const programChecklists: Record<string, ProgramChecklistData> = {
  pmkvy: {
    programId: "pmkvy",
    programName: "PMKVY",
    documents: [
      { id: "aadhar", label: "Aadhaar Card", description: "Original + photocopy" },
      { id: "photo", label: "Passport Size Photos", description: "4 recent photos (white background)" },
      { id: "marksheet", label: "10th / 12th Marksheet", description: "Original + photocopy" },
      { id: "bank", label: "Bank Passbook", description: "With account number & IFSC" },
      { id: "address", label: "Address Proof", description: "Ration card, electricity bill, or voter ID" },
    ],
    steps: [
      { id: "register", label: "Register on Skill India Digital Hub", link: "https://www.skillindiadigital.gov.in/home", linkLabel: "Register Now" },
      { id: "search", label: "Find nearest training center", link: "https://www.skillindiadigital.gov.in/pmkvy-landing", linkLabel: "Find Centers" },
      { id: "visit", label: "Visit training center with documents" },
      { id: "enroll", label: "Complete enrollment & biometric registration" },
      { id: "attend", label: "Attend training sessions (3–6 months)" },
      { id: "assess", label: "Pass assessment exam" },
      { id: "certificate", label: "Receive certificate & placement support" },
    ],
  },
  naps: {
    programId: "naps",
    programName: "NAPS",
    documents: [
      { id: "aadhar", label: "Aadhaar Card", description: "Linked to mobile number" },
      { id: "photo", label: "Passport Size Photos", description: "4 recent photos" },
      { id: "marksheet", label: "Education Certificates", description: "10th/12th/ITI marksheets" },
      { id: "bank", label: "Bank Account Details", description: "For stipend transfer" },
      { id: "medical", label: "Medical Fitness Certificate", description: "From any registered doctor" },
    ],
    steps: [
      { id: "register", label: "Register on Apprenticeship India Portal", link: "https://www.apprenticeshipindia.gov.in", linkLabel: "Register" },
      { id: "profile", label: "Complete your profile & upload documents" },
      { id: "search", label: "Search & apply to apprenticeship vacancies" },
      { id: "interview", label: "Attend interview at the establishment" },
      { id: "contract", label: "Sign apprenticeship contract" },
      { id: "training", label: "Start apprenticeship training (6–36 months)" },
      { id: "stipend", label: "Receive monthly stipend (₹5,000–₹9,000)" },
    ],
  },
  "skill-india": {
    programId: "skill-india",
    programName: "Skill India Digital",
    documents: [
      { id: "aadhar", label: "Aadhaar Card" },
      { id: "mobile", label: "Mobile Number", description: "Linked to Aadhaar" },
      { id: "email", label: "Email ID", description: "Active email for login" },
    ],
    steps: [
      { id: "register", label: "Create account on Skill India Digital", link: "https://skillindiadigital.gov.in", linkLabel: "Sign Up" },
      { id: "browse", label: "Browse courses by sector & interest" },
      { id: "enroll", label: "Enroll in chosen course (free)" },
      { id: "complete", label: "Complete all modules & quizzes" },
      { id: "certificate", label: "Download your certificate" },
    ],
  },
  "google-dm": {
    programId: "google-dm",
    programName: "Google Digital Marketing",
    documents: [
      { id: "email", label: "Google Account", description: "Gmail ID required" },
    ],
    steps: [
      { id: "login", label: "Sign in to Google Skillshop", link: "https://skillshop.withgoogle.com", linkLabel: "Go to Skillshop" },
      { id: "enroll", label: "Enroll in 'Fundamentals of Digital Marketing'" },
      { id: "modules", label: "Complete all 26 modules" },
      { id: "quiz", label: "Pass the final assessment (40 questions)" },
      { id: "certificate", label: "Download Google-certified certificate" },
    ],
  },
  freecodecamp: {
    programId: "freecodecamp",
    programName: "FreeCodeCamp",
    documents: [
      { id: "email", label: "Email ID", description: "For account creation" },
    ],
    steps: [
      { id: "register", label: "Create free account", link: "https://www.freecodecamp.org/learn", linkLabel: "Start Learning" },
      { id: "html", label: "Complete Responsive Web Design certification" },
      { id: "js", label: "Complete JavaScript Algorithms certification" },
      { id: "projects", label: "Build 5 certification projects" },
      { id: "certificate", label: "Claim your certificates" },
    ],
  },
  "tcs-ion": {
    programId: "tcs-ion",
    programName: "TCS iON Career Edge",
    documents: [
      { id: "email", label: "Email ID" },
      { id: "mobile", label: "Mobile Number" },
    ],
    steps: [
      { id: "register", label: "Register on TCS iON", link: "https://www.tcsion.com/courses/career-edge-young-professional/", linkLabel: "Register" },
      { id: "enroll", label: "Enroll in Career Edge – Young Professional" },
      { id: "modules", label: "Complete all learning modules" },
      { id: "quiz", label: "Pass assessments" },
      { id: "certificate", label: "Download TCS certificate" },
    ],
  },
  nptel: {
    programId: "nptel",
    programName: "NPTEL / SWAYAM",
    documents: [
      { id: "email", label: "Email ID" },
      { id: "aadhar", label: "Aadhaar / ID Proof", description: "For proctored exam (if opted)" },
    ],
    steps: [
      { id: "register", label: "Create account on SWAYAM", link: "https://swayam.gov.in", linkLabel: "Sign Up" },
      { id: "browse", label: "Browse and enroll in a course" },
      { id: "watch", label: "Watch weekly video lectures" },
      { id: "assignments", label: "Complete weekly assignments" },
      { id: "exam", label: "Appear for proctored exam (optional, for certificate)" },
      { id: "certificate", label: "Receive IIT/IISc certificate" },
    ],
  },
  hssc: {
    programId: "hssc",
    programName: "Healthcare Training (HSSC)",
    documents: [
      { id: "aadhar", label: "Aadhaar Card" },
      { id: "marksheet", label: "12th Marksheet", description: "Science stream preferred" },
      { id: "photo", label: "Passport Size Photos" },
      { id: "medical", label: "Medical Fitness Certificate" },
    ],
    steps: [
      { id: "search", label: "Find HSSC training centers", link: "https://www.healthcare-ssc.in/", linkLabel: "Visit HSSC" },
      { id: "register", label: "Register at the training center" },
      { id: "enroll", label: "Submit documents & enroll" },
      { id: "training", label: "Complete 3–6 month training" },
      { id: "assess", label: "Pass skill assessment" },
      { id: "placement", label: "Get hospital placement support" },
    ],
  },
  "ibm-skillsbuild": {
    programId: "ibm-skillsbuild",
    programName: "IBM SkillsBuild",
    documents: [
      { id: "email", label: "Email ID" },
    ],
    steps: [
      { id: "register", label: "Create IBM SkillsBuild account", link: "https://skillsbuild.org", linkLabel: "Sign Up" },
      { id: "path", label: "Choose a learning path (AI, Cloud, Cyber)" },
      { id: "complete", label: "Complete course modules" },
      { id: "badge", label: "Earn IBM Digital Badge" },
    ],
  },
  canva: {
    programId: "canva",
    programName: "Canva Design School",
    documents: [
      { id: "email", label: "Email ID or Google Account" },
    ],
    steps: [
      { id: "register", label: "Create free Canva account", link: "https://www.canva.com/designschool/", linkLabel: "Start Learning" },
      { id: "courses", label: "Complete beginner design courses" },
      { id: "practice", label: "Create 5+ design projects" },
      { id: "portfolio", label: "Build your design portfolio" },
      { id: "certificate", label: "Earn Canva certificates" },
    ],
  },
};

// Fallback for programs without specific checklist
const defaultChecklist: Omit<ProgramChecklistData, "programId" | "programName"> = {
  documents: [
    { id: "aadhar", label: "Aadhaar Card" },
    { id: "photo", label: "Passport Size Photos" },
    { id: "marksheet", label: "Education Certificates" },
    { id: "email", label: "Email ID" },
  ],
  steps: [
    { id: "visit", label: "Visit the program website" },
    { id: "register", label: "Create an account / register" },
    { id: "enroll", label: "Enroll in the course" },
    { id: "complete", label: "Complete all modules" },
    { id: "certificate", label: "Claim your certificate" },
  ],
};

const STORAGE_KEY = "wehelp_checklist_progress";

function loadProgress(): Record<string, string[]> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function saveProgress(progress: Record<string, string[]>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
}

export function ProgramChecklist({ programId, programName }: { programId: string; programName: string }) {
  const [expanded, setExpanded] = useState(false);
  const [checked, setChecked] = useState<string[]>([]);

  const data = programChecklists[programId] || {
    ...defaultChecklist,
    programId,
    programName,
  };

  const allItems = [...data.documents.map(d => `doc-${d.id}`), ...data.steps.map(s => `step-${s.id}`)];
  const totalItems = allItems.length;
  const completedCount = checked.length;
  const progressPct = totalItems > 0 ? Math.round((completedCount / totalItems) * 100) : 0;

  useEffect(() => {
    const all = loadProgress();
    setChecked(all[programId] || []);
  }, [programId]);

  const toggle = (itemId: string) => {
    setChecked(prev => {
      const next = prev.includes(itemId) ? prev.filter(x => x !== itemId) : [...prev, itemId];
      const all = loadProgress();
      all[programId] = next;
      saveProgress(all);
      return next;
    });
  };

  return (
    <Card className="border border-border bg-card">
      <CardContent className="p-0">
        {/* Header — always visible */}
        <button
          onClick={() => setExpanded(e => !e)}
          className="w-full flex items-center gap-3 p-4 text-left hover:bg-accent/30 transition-colors rounded-t-lg"
        >
          <FileText className="h-5 w-5 text-primary shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-foreground">Application Checklist</p>
            <div className="flex items-center gap-2 mt-1">
              <Progress value={progressPct} className="h-1.5 flex-1" />
              <span className="text-xs font-semibold text-primary whitespace-nowrap">{completedCount}/{totalItems}</span>
            </div>
          </div>
          {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </button>

        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              <div className="px-4 pb-4 space-y-4">
                {/* Documents */}
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">📄 Documents Needed</p>
                  <div className="space-y-1.5">
                    {data.documents.map(item => {
                      const key = `doc-${item.id}`;
                      const done = checked.includes(key);
                      return (
                        <button
                          key={key}
                          onClick={() => toggle(key)}
                          className={`w-full flex items-center gap-3 p-2.5 rounded-lg text-left transition-all border ${
                            done ? "bg-primary/5 border-primary/20" : "bg-card border-border hover:border-primary/30"
                          }`}
                        >
                          {done ? (
                            <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                          ) : (
                            <Circle className="h-4 w-4 text-muted-foreground shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${done ? "line-through text-muted-foreground" : "text-foreground"}`}>{item.label}</p>
                            {item.description && (
                              <p className="text-xs text-muted-foreground">{item.description}</p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Steps */}
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">📋 Application Steps</p>
                  <div className="space-y-1.5">
                    {data.steps.map((item, idx) => {
                      const key = `step-${item.id}`;
                      const done = checked.includes(key);
                      return (
                        <div key={key} className="flex items-start gap-2">
                          <button
                            onClick={() => toggle(key)}
                            className={`flex-1 flex items-center gap-3 p-2.5 rounded-lg text-left transition-all border ${
                              done ? "bg-primary/5 border-primary/20" : "bg-card border-border hover:border-primary/30"
                            }`}
                          >
                            {done ? (
                              <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                            ) : (
                              <div className="h-4 w-4 rounded-full border-2 border-muted-foreground shrink-0 flex items-center justify-center">
                                <span className="text-[9px] font-bold text-muted-foreground">{idx + 1}</span>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium ${done ? "line-through text-muted-foreground" : "text-foreground"}`}>{item.label}</p>
                              {item.description && (
                                <p className="text-xs text-muted-foreground">{item.description}</p>
                              )}
                            </div>
                          </button>
                          {item.link && (
                            <a
                              href={item.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="shrink-0 mt-2 inline-flex items-center gap-1 text-xs text-primary font-medium hover:underline"
                            >
                              {item.linkLabel || "Open"} <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {progressPct === 100 && (
                  <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 text-center">
                    <p className="text-sm font-bold text-primary">🎉 All done! You're ready to start {programName}.</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}
